<script setup lang="ts">
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import { socketEmitAsync } from '@/utils/web-socket'
import { useUserStore } from '@/stores/modules/user'
import type { PetType } from '@/types/pet'
import { getPetName, getPetPrice } from '../data'
import { equipQualityText } from '@/views/item/data'
import type { UserAttrType } from '@/types/user'
import { computed } from 'vue'
import { ElMessage } from 'element-plus'
import { computedTotalAttrValue } from '../data'
import { formatNumberWithCommas } from '@/utils'
import { Lock, Unlock } from '@element-plus/icons-vue'

const userStore = useUserStore()
const props = withDefaults(
  defineProps<{ petInfo: PetType; isInItems?: boolean; isInComparison?: boolean; showAction?: boolean; showLock?: boolean }>(),
  {
    isInItems: true,
    isInComparison: false,
    showAction: true,
    showLock: true,
  },
)
const emits = defineEmits(['clear', 'compare', 'closeCompare', 'sell'])

const outsidePetIdList = computed(() => {
  return userStore.outsidePetIdList.map((item) => userStore.petList.find((pet) => pet.id === item))
})

async function setPetFight(id: string) {
  const userAttr = await socketEmitAsync<UserAttrType>({ type: 'setPetFight', data: { id } })
  userStore.fightPetId = id
  userStore.userAttr = userAttr
  emits('clear')
}
async function setPetOutside(id: string, index: number) {
  await socketEmitAsync({ type: 'setPetOutside', data: { id, index } })
  userStore.outsidePetIdList[index] = id
  emits('clear')
}
async function setPetBindRoutine(id: string, routeId: number) {
  // 如点击已绑定的套路则为解绑逻辑
  const bindRoutineInfo = userStore.equippedRoutineList.find((item) => item.id === routeId)
  if (bindRoutineNameList.value.includes(bindRoutineInfo?.name || '')) id = ''

  await socketEmitAsync({ type: 'setPetBindRoutine', data: { id, routeId } })

  if (id !== '') {
    const petInfo = userStore.petList.find((item) => item.id === id)
    if (!petInfo) return
  }

  const routineInfo = userStore.equippedRoutineList.find((item) => item.id === routeId)
  if (!routineInfo) return
  routineInfo.fightPetId = id

  ElMessage.success(id ? '绑定成功' : '解除绑定成功')
  // emits('clear')
}
function setPetRest() {
  const id = props.petInfo.id
  if (userStore.fightPetId === id) {
    setPetFight('')
  } else {
    const index = userStore.outsidePetIdList.findIndex((item) => item === id)
    setPetOutside('', index)
  }
  emits('clear')
}
function sellPet(item: PetType) {
  if (item.isLock) return ElMessage.warning('无法出售已锁定的魔物')
  ElMessageBox.confirm(
    `<p class="affix">确定出售<span class="${item.quality}">${getPetName(
      item.monsterId,
    )}</span>吗？<span class="shrink">（魔物十分珍贵，请谨慎操作）</span></p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const id = item.id
      await socketEmitAsync({ type: 'sellPet', data: { idList: [id] } })
      const index = userStore.petList.findIndex((item) => item.id === id)
      userStore.petList.splice(index, 1)
      const price = getPetPrice(props.petInfo.quality)
      userStore.gold += price

      emits('clear')
      ElMessage.success('出售成功')
    })
    .catch(() => {})
}

const bindRoutineNameList = computed(() => {
  const fightPetId = props.petInfo.id
  const routineInfo = userStore.equippedRoutineList.filter((item) => item.fightPetId === fightPetId)
  if (!routineInfo) return ''
  return routineInfo.map((item) => item.name)
})

async function handleLock() {
  const id = props.petInfo.id
  await socketEmitAsync({ type: 'lockPet', data: { id } })
  const pet = userStore.petList.find((item) => item.id === id)
  if (!pet) return
  pet.isLock = !pet.isLock
}
</script>

<template>
  <div class="pet-info affix">
    <p :class="petInfo.quality" style="position: relative; margin-bottom: 8px; font-weight: bold">
      <span>{{ getPetName(petInfo.monsterId) }}</span>
      <span v-show="showAction && showLock" style="position: absolute; right: 4px; top: calc(100% + 10px)">
        <el-button size="small" :type="petInfo.isLock ? 'success' : 'info'" plain @click="handleLock">
          <el-icon size="20" v-show="!petInfo.isLock"><Unlock /></el-icon>
          <el-icon size="20" v-show="petInfo.isLock"><Lock /></el-icon>
        </el-button>
      </span>
    </p>
    <p>
      品质：
      <span :class="petInfo.quality">{{ equipQualityText[petInfo?.quality || 'normal'] }} · {{ petInfo.monsterId.toString().slice(0, 1) }}阶 </span>
    </p>
    <p>
      售价：<span class="gold">{{ formatNumberWithCommas(getPetPrice(petInfo.quality)) }}</span>
    </p>
    <p :class="userStore.fightPetId === petInfo.id ? 'equipped' : ''">
      出战属性：<span :class="petInfo.quality" style="font-size: 12px; margin-right: 5px"
        >( 评分：{{ computedTotalAttrValue(petInfo.fightAttrs).toFixed(1) }} )
      </span>
    </p>
    <p v-if="bindRoutineNameList && bindRoutineNameList.length" style="font-size: 12px" class="equipped">
      绑定套路：{{ bindRoutineNameList.join('、') }}
    </p>
    <p v-if="petInfo.fightAttrs.atk && petInfo.fightAttrs.atk >= 0.5">
      攻击 + <span class="grow">{{ Math.round(petInfo.fightAttrs.atk) }}</span>
    </p>
    <p v-if="petInfo.fightAttrs.atksp && petInfo.fightAttrs.atksp >= 0.05">
      攻击速度 + <span class="grow">{{ petInfo.fightAttrs.atksp.toFixed(1) }}%</span>
    </p>
    <p v-if="petInfo.fightAttrs.crt && petInfo.fightAttrs.crt >= 0.05">
      暴击率 + <span class="grow">{{ petInfo.fightAttrs.crt.toFixed(1) }}%</span>
    </p>
    <p v-if="petInfo.fightAttrs.crtd && petInfo.fightAttrs.crtd >= 0.05">
      暴击伤害 + <span class="grow">{{ petInfo.fightAttrs.crtd.toFixed(1) }}%</span>
    </p>
    <p v-if="petInfo.fightAttrs.heat && petInfo.fightAttrs.heat >= 0.5">
      破防 + <span class="grow">{{ Math.round(petInfo.fightAttrs.heat) }}</span>
    </p>
    <p v-if="petInfo.fightAttrs.hr && petInfo.fightAttrs.hr >= 0.05">
      命中率 + <span class="grow">{{ petInfo.fightAttrs.hr.toFixed(1) }}%</span>
    </p>
    <template v-if="petInfo.outsideAttrs">
      <br />
      <p :class="userStore.outsidePetIdList.includes(petInfo.id) ? 'equipped' : ''">外出属性：</p>
      <p>
        金币
        <span>
          + <span class="grow">{{ petInfo.outsideAttrs.gold }}</span>
        </span>
      </p>
    </template>
  </div>
  <el-row :gutter="10" class="pet-action" v-show="isInItems && showAction">
    <el-col :span="8">
      <CommonBtn type="success" size="small" @done="setPetFight(petInfo.id)">出战</CommonBtn>
    </el-col>
    <el-col :span="8">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn color="#ffb3a7" size="small" dark>外出</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button
            v-for="(item, index) of outsidePetIdList"
            :type="item ? 'primary' : 'info'"
            size="small"
            plain
            @click="setPetOutside(petInfo.id, index)"
          >
            <span v-if="item" :class="item.quality">{{ getPetName(item.monsterId) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <CommonBtn type="primary" size="small" @done="emits('compare')">比对</CommonBtn>
    </el-col>
  </el-row>
  <el-row :gutter="10" class="pet-action" style="margin-top: 5px" v-show="isInItems && showAction">
    <el-col :span="8" v-if="!isInComparison">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="primary" size="small">绑定</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button
            v-for="item of userStore.equippedRoutineList"
            :type="bindRoutineNameList?.includes(item.name) ? 'success' : 'primary'"
            size="small"
            plain
            @click="setPetBindRoutine(petInfo.id, item.id)"
          >
            <span>{{ item.name }}</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <!-- <el-col :span="8" v-if="!isInComparison && bindRoutineNameList">
      <CommonBtn type="danger" size="small" @done="setPetBindRoutine(petInfo.id, 0)">解绑</CommonBtn>
    </el-col> -->
    <el-col :span="8" v-if="!isInComparison">
      <CommonBtn type="warning" size="small" @done="sellPet(petInfo)">出售</CommonBtn>
    </el-col>
  </el-row>
  <el-row class="pet-action" v-show="!isInItems && showAction">
    <el-col :span="8">
      <CommonBtn type="danger" size="small" @done="setPetRest">休息</CommonBtn>
    </el-col>
  </el-row>
</template>

<style lang="scss">
.pet-info {
  font-size: 14px;
  height: 100%;
  overflow: auto;
  margin-bottom: 10px;
  p {
    margin: 4px 0;
  }
  .pet-lock {
    position: absolute;
    right: 4px;
    top: calc(100% + 10px);
  }
}
.hour-list {
  display: flex;
  flex-flow: column nowrap;
  button {
    width: 100%;
    margin: 4px 0;
    margin-left: 0 !important;
  }
}
.pet-action {
  .el-popper {
    max-height: 38vh;
    overflow: auto;
  }
}
</style>
